import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Input } from './ui/input';
import { MapPin, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Production-grade AddressAutocomplete component
 * Uses Geocoder API as fallback when place_changed doesn't fire
 */
const AddressAutocomplete = forwardRef(({ 
  value, 
  onChange, 
  onAddressSelect,
  onValidationComplete,
  placeholder = "Start typing an address...",
  className = "",
  enableValidation = true,
  validateOnSelect = true,
  ...props 
}, ref) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const geocoderRef = useRef(null);
  const isProcessingRef = useRef(false);
  const lastInputValueRef = useRef('');
  const blurTimeoutRef = useRef(null);
  
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isValidating, setIsValidating] = useState(false);
  const [validationStatus, setValidationStatus] = useState(null);
  const [validationMessage, setValidationMessage] = useState('');

  /**
   * Reset the autocomplete state - exposed via ref
   */
  const resetAutocomplete = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    lastInputValueRef.current = '';
    
    setValidationStatus(null);
    setValidationMessage('');
    isProcessingRef.current = false;
    
    if (onChange) {
      onChange({ target: { value: '' } });
    }
    
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
    
    console.log('AddressAutocomplete: Reset complete');
  }, [onChange]);

  useImperativeHandle(ref, () => ({
    reset: resetAutocomplete,
    focus: () => inputRef.current?.focus(),
    blur: () => inputRef.current?.blur()
  }), [resetAutocomplete]);

  /**
   * Parse address components from Google result
   */
  const parseAddressComponents = useCallback((result) => {
    if (!result || !result.address_components) {
      return null;
    }

    const addressData = {
      street: '',
      apartment: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'United States',
      formatted_address: result.formatted_address || '',
      latitude: result.geometry?.location?.lat?.() || result.geometry?.location?.lat || null,
      longitude: result.geometry?.location?.lng?.() || result.geometry?.location?.lng || null,
      place_id: result.place_id || null
    };

    let streetNumber = '';
    let streetName = '';

    for (const component of result.address_components) {
      const types = component.types;
      
      if (types.includes('street_number')) {
        streetNumber = component.long_name;
      }
      if (types.includes('route')) {
        streetName = component.long_name;
      }
      if (types.includes('subpremise')) {
        addressData.apartment = component.long_name;
      }
      if (types.includes('locality')) {
        addressData.city = component.long_name;
      } else if (!addressData.city && types.includes('sublocality_level_1')) {
        addressData.city = component.long_name;
      } else if (!addressData.city && types.includes('sublocality')) {
        addressData.city = component.long_name;
      } else if (!addressData.city && types.includes('neighborhood')) {
        addressData.city = component.long_name;
      } else if (!addressData.city && types.includes('administrative_area_level_3')) {
        addressData.city = component.long_name;
      } else if (!addressData.city && types.includes('administrative_area_level_2')) {
        addressData.city = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        addressData.state = component.short_name;
      }
      if (types.includes('postal_code')) {
        addressData.zip_code = component.long_name;
      }
      if (types.includes('postal_code_suffix')) {
        addressData.zip_plus4 = component.long_name;
      }
      if (types.includes('country')) {
        addressData.country = component.long_name;
        addressData.country_code = component.short_name;
      }
    }

    addressData.street = `${streetNumber} ${streetName}`.trim();
    
    if (!addressData.street && result.formatted_address) {
      const parts = result.formatted_address.split(',');
      if (parts.length > 0) {
        addressData.street = parts[0].trim();
      }
    }

    return addressData;
  }, []);

  /**
   * Validate address using Smarty API (server-side)
   */
  const validateAddress = useCallback(async (addressData) => {
    if (!enableValidation || !addressData) return addressData;
    
    if (addressData.country_code && addressData.country_code !== 'US') {
      return addressData;
    }

    setIsValidating(true);
    setValidationStatus(null);
    setValidationMessage('');

    try {
      const response = await fetch(`${API_URL}/api/address/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          street: addressData.street,
          city: addressData.city,
          state: addressData.state,
          zip_code: addressData.zip_code
        })
      });

      const result = await response.json();

      if (response.ok && result.valid) {
        const normalizedAddress = {
          ...addressData,
          street: result.normalized?.street || addressData.street,
          city: result.normalized?.city || addressData.city,
          state: result.normalized?.state || addressData.state,
          zip_code: result.normalized?.zip_code || addressData.zip_code,
          zip_plus4: result.normalized?.zip_plus4 || addressData.zip_plus4,
        };

        setValidationStatus('valid');
        setValidationMessage(result.corrected ? (result.message || 'Address found') : 'Address verified');
        
        if (onValidationComplete) {
          onValidationComplete(normalizedAddress, true);
        }
        
        return normalizedAddress;
      } else if (result.normalized) {
        const correctedAddress = {
          ...addressData,
          street: result.normalized?.street || addressData.street,
          city: result.normalized?.city || addressData.city,
          state: result.normalized?.state || addressData.state,
          zip_code: result.normalized?.zip_code || addressData.zip_code
        };

        setValidationStatus('warning');
        setValidationMessage(result.message || 'Please verify this address');
        
        if (onValidationComplete) {
          onValidationComplete(correctedAddress, true);
        }
        
        return correctedAddress;
      } else {
        setValidationStatus('warning');
        setValidationMessage(result.message || 'Could not verify address');
        
        if (onValidationComplete) {
          onValidationComplete(addressData, false);
        }
        
        return addressData;
      }
    } catch (error) {
      console.error('AddressAutocomplete: Validation error:', error);
      setValidationStatus(null);
      if (onValidationComplete) {
        onValidationComplete(addressData, null);
      }
      return addressData;
    } finally {
      setIsValidating(false);
    }
  }, [enableValidation, onValidationComplete]);

  /**
   * Process address data and notify parent
   */
  const processAddressData = useCallback(async (addressData) => {
    if (!addressData) return;
    
    if (isProcessingRef.current) {
      console.log('AddressAutocomplete: Already processing, skipping');
      return;
    }
    
    isProcessingRef.current = true;
    console.log('AddressAutocomplete: Processing address data:', addressData);

    try {
      if (inputRef.current && addressData.street) {
        inputRef.current.value = addressData.street;
        lastInputValueRef.current = addressData.street;
      }
      
      if (onChange) {
        onChange({ target: { value: addressData.street } });
      }

      if (validateOnSelect && enableValidation) {
        addressData = await validateAddress(addressData);
      }

      if (onAddressSelect) {
        console.log('AddressAutocomplete: Calling onAddressSelect');
        onAddressSelect(addressData);
      }
    } finally {
      setTimeout(() => {
        isProcessingRef.current = false;
      }, 500);
    }
  }, [onChange, onAddressSelect, validateAddress, validateOnSelect, enableValidation]);

  /**
   * Use Geocoder to get address details from text
   */
  const geocodeAddress = useCallback((addressText) => {
    return new Promise((resolve) => {
      if (!geocoderRef.current || !addressText) {
        resolve(null);
        return;
      }

      console.log('AddressAutocomplete: Geocoding address:', addressText);
      
      geocoderRef.current.geocode(
        { address: addressText, componentRestrictions: { country: 'US' } },
        (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            console.log('AddressAutocomplete: Geocode success');
            resolve(results[0]);
          } else {
            console.log('AddressAutocomplete: Geocode failed:', status);
            resolve(null);
          }
        }
      );
    });
  }, []);

  /**
   * Handle blur - check if value changed and geocode if needed
   */
  const handleBlur = useCallback(async () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    
    // Small delay to allow place_changed to fire first
    blurTimeoutRef.current = setTimeout(async () => {
      if (isProcessingRef.current) return;
      
      const currentValue = inputRef.current?.value || '';
      const previousValue = lastInputValueRef.current;
      
      // Check if value changed significantly (user selected from dropdown)
      if (currentValue && currentValue !== previousValue && currentValue.length > previousValue.length + 3) {
        console.log('AddressAutocomplete: Value changed on blur, geocoding...');
        
        // First try to get place from autocomplete
        if (autocompleteRef.current) {
          const place = autocompleteRef.current.getPlace();
          if (place && place.address_components) {
            const addressData = parseAddressComponents(place);
            if (addressData) {
              await processAddressData(addressData);
              return;
            }
          }
        }
        
        // Fallback: use Geocoder API
        const geocodeResult = await geocodeAddress(currentValue);
        if (geocodeResult) {
          const addressData = parseAddressComponents(geocodeResult);
          if (addressData) {
            await processAddressData(addressData);
            return;
          }
        }
        
        // Last resort: just update the street value
        lastInputValueRef.current = currentValue;
      }
    }, 150);
  }, [geocodeAddress, parseAddressComponents, processAddressData]);

  /**
   * Initialize Google Places Autocomplete
   */
  const initAutocomplete = useCallback(() => {
    if (!inputRef.current || !window.google?.maps?.places) {
      console.warn('AddressAutocomplete: Google Maps API not ready');
      return false;
    }

    try {
      // Initialize Geocoder for fallback
      geocoderRef.current = new window.google.maps.Geocoder();
      
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }

      autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: 'us' },
        fields: ['address_components', 'formatted_address', 'geometry', 'name', 'place_id'],
        types: ['address']
      });

      // Main event handler for place selection
      autocompleteRef.current.addListener('place_changed', () => {
        console.log('AddressAutocomplete: place_changed event fired');
        
        // Clear any pending blur timeout
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
          blurTimeoutRef.current = null;
        }
        
        const place = autocompleteRef.current?.getPlace();
        if (place && place.address_components) {
          const addressData = parseAddressComponents(place);
          if (addressData) {
            processAddressData(addressData);
          }
        }
      });

      console.log('AddressAutocomplete: Google Places initialized successfully');
      return true;
    } catch (error) {
      console.error('AddressAutocomplete: Initialization error:', error);
      return false;
    }
  }, [parseAddressComponents, processAddressData]);

  /**
   * Check and wait for Google Maps API to load using the async callback pattern
   */
  useEffect(() => {
    let isMounted = true;

    const initializeWhenReady = () => {
      if (!isMounted) return;
      
      if (window.google?.maps?.places) {
        setIsLoaded(true);
        const success = initAutocomplete();
        if (success) {
          setIsInitializing(false);
        }
      }
    };

    // Check if already loaded (googleMapsReady is set by our callback)
    if (window.googleMapsReady && window.google?.maps?.places) {
      initializeWhenReady();
    } else {
      // Listen for the custom event fired when Google Maps is ready
      const handleGoogleMapsReady = () => {
        // Small delay to ensure places library is fully initialized
        setTimeout(initializeWhenReady, 100);
      };
      
      window.addEventListener('google-maps-ready', handleGoogleMapsReady);
      
      // Fallback: also poll in case the event already fired before listener was added
      let attempts = 0;
      const maxAttempts = 50;
      const pollInterval = setInterval(() => {
        attempts++;
        if (window.google?.maps?.places) {
          clearInterval(pollInterval);
          initializeWhenReady();
        } else if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          console.error('AddressAutocomplete: Google Maps API failed to load after timeout');
          if (isMounted) setIsInitializing(false);
        }
      }, 200);
      
      return () => {
        isMounted = false;
        clearInterval(pollInterval);
        window.removeEventListener('google-maps-ready', handleGoogleMapsReady);
        if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
        if (autocompleteRef.current) {
          window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
        }
      };
    }

    return () => {
      isMounted = false;
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [initAutocomplete]);

  /**
   * Handle input changes
   */
  const handleInputChange = useCallback((e) => {
    setValidationStatus(null);
    setValidationMessage('');
    if (onChange) {
      onChange(e);
    }
  }, [onChange]);

  // Sync input value with prop when value changes externally
  useEffect(() => {
    if (inputRef.current && value !== undefined && !isProcessingRef.current) {
      if (inputRef.current.value !== value) {
        inputRef.current.value = value;
        lastInputValueRef.current = value;
      }
    }
  }, [value]);

  const getStatusIcon = () => {
    if (isInitializing || isValidating) {
      return <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />;
    }
    if (validationStatus === 'valid') {
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
    if (validationStatus === 'invalid') {
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    }
    if (validationStatus === 'warning') {
      return <AlertCircle className="h-4 w-4 text-amber-500" />;
    }
    return <MapPin className="h-4 w-4 text-gray-400" />;
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        defaultValue={value}
        onChange={handleInputChange}
        onBlur={handleBlur}
        placeholder={isInitializing ? "Loading address lookup..." : placeholder}
        className={`pr-10 ${validationStatus === 'invalid' ? 'border-red-500' : ''} ${validationStatus === 'valid' ? 'border-green-500' : ''} ${className}`}
        autoComplete="off"
        disabled={isInitializing}
        aria-describedby={validationMessage ? "address-validation-message" : undefined}
        {...props}
      />
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        {getStatusIcon()}
      </div>
      {validationMessage && (
        <p 
          id="address-validation-message"
          className={`text-xs mt-1 ${
            validationStatus === 'valid' ? 'text-green-600' : 
            validationStatus === 'invalid' ? 'text-red-600' : 
            'text-amber-600'
          }`}
        >
          {validationMessage}
        </p>
      )}
    </div>
  );
});

AddressAutocomplete.displayName = 'AddressAutocomplete';

export default AddressAutocomplete;
